"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, UserCheck, MessageSquare, Target, Compass, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function IndependentConsultant() {
  const shouldReduceMotion = useReducedMotion();
  const reasons = [{
    title: "Personal Strategic Involvement",
    description: "I design and oversee your strategy directly. Every audit, recommendation and commercial priority is shaped by my own analysis.",
    icon: UserCheck
  }, {
    title: "Clear Communication",
    description: "You deal with me, not a chain of account managers. Conversations stay focused on commercial context, buyer behaviour and realistic priorities.",
    icon: MessageSquare
  }, {
    title: "Commercial Focus",
    description: "My recommendations centre on qualified enquiries, pipeline relevance and sales-cycle fit rather than vanity traffic numbers.",
    icon: Target
  }, {
    title: "Tailored Recommendations",
    description: "There are no recycled playbooks or automated checklist dumps. Your strategy reflects your solutions, buying committee dynamics and competitive field.",
    icon: Compass
  }, {
    title: "Continuity & Accountability",
    description: "The same consultant who audits your architecture carries the work through strategy, content briefs and ongoing performance reviews.",
    icon: RefreshCw
  }];
  return <section id="independentconsultant" data-nav="light" className="bg-muted/40 text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            Direct Expert Advisory
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Why work with an independent B2B SEO consultant
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            You work directly with the person responsible for your strategy. There is no delegation to junior staff, no dilution of context, and no guesswork about who is doing the work.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {reasons.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.title} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: index * 0.08
          }}>
                <Card className="h-full border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs">
                  <CardHeader className="pb-3">
                    <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 sm:p-10 text-center max-w-3xl mx-auto shadow-sm">
          <p className="text-base sm:text-lg text-foreground font-medium mb-3">
            Whether you want to{" "}
            <Link to="/HireSeoConsultantPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              hire an SEO consultant directly
            </Link>{" "}
            for focused advisory or retain strategic support through{" "}
            <Link to="/MonthlySeoServicesPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              Monthly SEO Services
            </Link>
            , I provide straightforward guidance built around your pipeline.
          </p>
          <div className="mt-6 flex justify-center">
            <Button asChild size="lg" className="w-full sm:w-auto rounded-full font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}